import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../api/axios";
import { FaUser, FaPhoneAlt, FaBriefcase, FaClock, FaCheckCircle, FaArrowRight, FaArrowLeft } from "react-icons/fa";

const EmployeeWizard = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    profile: {
      first_name: "",
      middle_name: "",
      last_name: "",
      father_name: "",
      age: "",
      gender: "",
      blood_group: "",
      date_of_birth: "",
      height: "",
      weight: "",
      education: "",
      profile_color: "#0077cc",
    },
    contact: {
      nationality: "",
      office_email: "",
      personal_email: "",
      office_mobile: "",
      personal_mobile: "",
      whatsapp_no: "",
      office_telephone: "",
      personal_telephone: "",
      home_country_address: "",
      current_address: "",
      emergency_contacts: [
        { name: "", relation_type: "", address: "", phone: "", email: "" },
      ],
    },
    employment: {
      branch: "",
      designation: "",
      employee_type: "",
      date_of_joining: "",
      is_active: true,
    },
    timing: {
      period_from: "",
      period_to: "",
      shift_start: "",
      shift_end: "",
      created_by: "1",
    },
    shift: {
      shift_name: "",
      start_time: "",
      end_time: "",
      total_hours: "",
    },
  });

  const nextStep = () => setStep((prev) => prev + 1);
  const prevStep = () => setStep((prev) => prev - 1);

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      profile: { ...prev.profile, [name]: value },
    }));
  };

  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      contact: { ...prev.contact, [name]: value },
    }));
  };

  const handleEmergencyChange = (index, e) => {
    const { name, value } = e.target;
    const updatedContacts = [...formData.contact.emergency_contacts];
    updatedContacts[index][name] = value;
    setFormData((prev) => ({
      ...prev,
      contact: { ...prev.contact, emergency_contacts: updatedContacts },
    }));
  };

  const addEmergencyContact = () => {
    setFormData((prev) => ({
      ...prev,
      contact: {
        ...prev.contact,
        emergency_contacts: [
          ...prev.contact.emergency_contacts,
          { name: "", relation_type: "", address: "", phone: "", email: "" },
        ],
      },
    }));
  };

  const removeEmergencyContact = (index) => {
    const updatedContacts = formData.contact.emergency_contacts.filter((_, i) => i !== index);
    setFormData((prev) => ({
      ...prev,
      contact: { ...prev.contact, emergency_contacts: updatedContacts },
    }));
  };

  const handleEmploymentChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      employment: {
        ...prev.employment,
        [name]: type === "checkbox" ? checked : value,
      },
    }));
  };

  const handleTimingChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      timing: { ...prev.timing, [name]: value },
    }));
  };

  const handleShiftChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      shift: { ...prev.shift, [name]: value },
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // 1. Save Profile
      const profilePayload = {
        ...formData.profile,
        age: formData.profile.age ? Number(formData.profile.age) : null,
        height: formData.profile.height ? Number(formData.profile.height) : null,
        weight: formData.profile.weight ? Number(formData.profile.weight) : null,
        date_of_birth: formData.profile.date_of_birth || null,
      };

      const profileRes = await axios.post("employees/", profilePayload);
      const employeeId = profileRes.data.id || profileRes.data.employee_id;

      if (!employeeId) throw new Error("Failed to get Employee ID from profile creation.");

      // Staff sync is handled separately via /api/staff/sync-employees
      // Direct staff creation skipped here to avoid DB enum constraint issues

      // 2. Save Contact
      const contactPayload = {
        ...formData.contact,
        employee_id: employeeId,
      };
      await axios.post("employees/contacts/", contactPayload);

      // 3. Save Employment
      const employmentPayload = {
        ...formData.employment,
        employee_id: employeeId,
      };
      await axios.post("employment/", employmentPayload);

      // 4. Save Timing
      if (formData.timing.period_from && formData.timing.period_to && formData.timing.shift_start && formData.timing.shift_end) {
        const timingPayload = {
          ...formData.timing,
          employee_id: employeeId,
          created_by: Number(formData.timing.created_by),
        };
        await axios.post("employees/timing/add-timing", timingPayload);
      }

      // 5. Save Shift — 'name' field maps to 'shift_name' in the form, backend schema expects 'name'
      if (formData.shift.shift_name && formData.shift.start_time && formData.shift.end_time) {
        const shiftPayload = {
          name: formData.shift.shift_name,
          start_time: formData.shift.start_time,
          end_time: formData.shift.end_time,
          total_hours: formData.shift.total_hours ? Number(formData.shift.total_hours) : null,
        };
        await axios.post("shifts/", shiftPayload);
      }

      alert("Full employee record saved successfully!");
      navigate("/dashboard/employees/list");
    } catch (err) {
      console.error(err);
      alert("Error saving employee record: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { id: 1, label: "Profile", icon: <FaUser /> },
    { id: 2, label: "Contact", icon: <FaPhoneAlt /> },
    { id: 3, label: "Employment", icon: <FaBriefcase /> },
    { id: 4, label: "Timings", icon: <FaClock /> },
  ];

  return (
    <div className="container mt-4" style={{ maxWidth: "1000px" }}>
      <div className="card shadow border-0" style={{ borderRadius: "15px", overflow: "hidden" }}>
        {/* Header */}
        <div style={{ background: "linear-gradient(135deg, #0077cc 0%, #004499 100%)", padding: "30px", color: "white" }}>
          <h2 className="mb-1" style={{ fontWeight: "700" }}>Add Employee Member</h2>
          <p className="mb-0 opacity-75">Fill in the details to register a new employee in the system.</p>
        </div>

        {/* Custom Progress Stepper */}
        <div style={{ background: "#f8f9fa", padding: "20px", borderBottom: "1px solid #eee" }}>
          <div className="d-flex justify-content-between position-relative" style={{ maxWidth: "800px", margin: "0 auto" }}>
            {/* Background Line */}
            <div style={{ 
              position: "absolute", 
              top: "20px", 
              left: "10%", 
              right: "10%", 
              height: "2px", 
              background: "#ddd", 
              zIndex: 1 
            }}></div>
            
            {steps.map((s) => (
              <div key={s.id} className="text-center" style={{ zIndex: 2, position: "relative" }}>
                <div style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "50%",
                  background: step >= s.id ? "#0077cc" : "#fff",
                  color: step >= s.id ? "#fff" : "#999",
                  border: step >= s.id ? "2px solid #0077cc" : "2px solid #ddd",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 8px",
                  transition: "all 0.3s ease",
                  boxShadow: step === s.id ? "0 0 0 4px rgba(0,119,204,0.2)" : "none"
                }}>
                  {step > s.id ? <FaCheckCircle /> : s.icon}
                </div>
                <span style={{ 
                  fontSize: "12px", 
                  fontWeight: step === s.id ? "600" : "400",
                  color: step >= s.id ? "#0077cc" : "#999"
                }}>{s.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="card-body p-4 p-md-5">
          {/* STEP 1: PERSONAL PROFILE */}
          {step === 1 && (
            <div className="animate__animated animate__fadeIn">
              <h4 className="mb-4 d-flex align-items-center" style={{ color: "#0077cc" }}>
                <FaUser className="me-2" /> Personal Profile
              </h4>
              <div className="row g-3">
                <div className="col-md-4">
                  <label className="form-label fw-bold">First Name *</label>
                  <input type="text" name="first_name" className="form-control form-control-lg border-2" value={formData.profile.first_name} onChange={handleProfileChange} required placeholder="Enter first name" />
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-bold">Middle Name</label>
                  <input type="text" name="middle_name" className="form-control form-control-lg" value={formData.profile.middle_name} onChange={handleProfileChange} placeholder="Enter middle name" />
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-bold">Last Name</label>
                  <input type="text" name="last_name" className="form-control form-control-lg" value={formData.profile.last_name} onChange={handleProfileChange} placeholder="Enter last name" />
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-bold">Father's Name</label>
                  <input type="text" name="father_name" className="form-control form-control-lg" value={formData.profile.father_name} onChange={handleProfileChange} placeholder="Enter father's name" />
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-bold">Date of Birth</label>
                  <input type="date" name="date_of_birth" className="form-control form-control-lg" value={formData.profile.date_of_birth} onChange={handleProfileChange} />
                </div>
                <div className="col-md-2">
                  <label className="form-label fw-bold">Age</label>
                  <input type="number" name="age" className="form-control form-control-lg" value={formData.profile.age} onChange={handleProfileChange} />
                </div>
                <div className="col-md-2">
                  <label className="form-label fw-bold">Gender</label>
                  <select name="gender" className="form-select form-select-lg" value={formData.profile.gender} onChange={handleProfileChange}>
                    <option value="">Select</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label fw-bold">Blood Group</label>
                  <select name="blood_group" className="form-select form-select-lg" value={formData.profile.blood_group} onChange={handleProfileChange}>
                    <option value="">Select</option>
                    <option value="A+">A+</option>
                    <option value="A-">A-</option>
                    <option value="B+">B+</option>
                    <option value="B-">B-</option>
                    <option value="AB+">AB+</option>
                    <option value="AB-">AB-</option>
                    <option value="O+">O+</option>
                    <option value="O-">O-</option>
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label fw-bold">Education</label>
                  <input type="text" name="education" className="form-control form-control-lg" value={formData.profile.education} onChange={handleProfileChange} placeholder="Highest qualification" />
                </div>
                <div className="col-md-3">
                  <label className="form-label fw-bold">Height (cm)</label>
                  <input type="number" name="height" className="form-control form-control-lg" value={formData.profile.height} onChange={handleProfileChange} />
                </div>
                <div className="col-md-3">
                  <label className="form-label fw-bold">Weight (kg)</label>
                  <input type="number" name="weight" className="form-control form-control-lg" value={formData.profile.weight} onChange={handleProfileChange} />
                </div>
              </div>
              <div className="d-flex justify-content-end mt-5">
                <button className="btn btn-primary btn-lg px-5 shadow-sm" onClick={nextStep} style={{ borderRadius: "10px" }}>
                  Next Step <FaArrowRight className="ms-2" />
                </button>
              </div>
            </div>
          )}

          {/* STEP 2: CONTACT INFORMATION */}
          {step === 2 && (
            <div className="animate__animated animate__fadeIn">
              <h4 className="mb-4 d-flex align-items-center" style={{ color: "#0077cc" }}>
                <FaPhoneAlt className="me-2" /> Contact Information
              </h4>
              <div className="row g-3">
                <div className="col-md-4">
                  <label className="form-label fw-bold">Nationality</label>
                  <input type="text" name="nationality" className="form-control form-control-lg" value={formData.contact.nationality} onChange={handleContactChange} placeholder="e.g. Saudi Arabian" />
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-bold">Personal Mobile</label>
                  <input type="text" name="personal_mobile" className="form-control form-control-lg" value={formData.contact.personal_mobile} onChange={handleContactChange} placeholder="+966..." />
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-bold">WhatsApp No</label>
                  <input type="text" name="whatsapp_no" className="form-control form-control-lg" value={formData.contact.whatsapp_no} onChange={handleContactChange} placeholder="Same as mobile?" />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-bold">Personal Email</label>
                  <input type="email" name="personal_email" className="form-control form-control-lg" value={formData.contact.personal_email} onChange={handleContactChange} placeholder="example@mail.com" />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-bold">Office Email</label>
                  <input type="email" name="office_email" className="form-control form-control-lg" value={formData.contact.office_email} onChange={handleContactChange} placeholder="work@rekonic.com" />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-bold">Current Address</label>
                  <textarea name="current_address" className="form-control form-control-lg" rows="2" value={formData.contact.current_address} onChange={handleContactChange} placeholder="Local residence address"></textarea>
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-bold">Home Country Address</label>
                  <textarea name="home_country_address" className="form-control form-control-lg" rows="2" value={formData.contact.home_country_address} onChange={handleContactChange} placeholder="Permanent address in home country"></textarea>
                </div>
              </div>

              <hr className="my-5" />
              <h5 className="mb-4">Emergency Contacts</h5>
              {formData.contact.emergency_contacts.map((contact, index) => (
                <div key={index} className="p-3 mb-3 border rounded bg-light position-relative">
                  <div className="row g-3">
                    <div className="col-md-3">
                      <label className="form-label">Name</label>
                      <input type="text" name="name" className="form-control" value={contact.name} onChange={(e) => handleEmergencyChange(index, e)} />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label">Relation</label>
                      <input type="text" name="relation_type" className="form-control" value={contact.relation_type} onChange={(e) => handleEmergencyChange(index, e)} />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Phone</label>
                      <input type="text" name="phone" className="form-control" value={contact.phone} onChange={(e) => handleEmergencyChange(index, e)} />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Email</label>
                      <input type="email" name="email" className="form-control" value={contact.email} onChange={(e) => handleEmergencyChange(index, e)} />
                    </div>
                    <div className="col-md-1 d-flex align-items-end">
                      {formData.contact.emergency_contacts.length > 1 && (
                        <button className="btn btn-outline-danger" onClick={() => removeEmergencyContact(index)} title="Remove Contact">×</button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              <button className="btn btn-outline-primary mt-2" onClick={addEmergencyContact}>+ Add Emergency Contact</button>

              <div className="d-flex justify-content-between mt-5">
                <button className="btn btn-secondary btn-lg px-4" onClick={prevStep} style={{ borderRadius: "10px" }}>
                  <FaArrowLeft className="me-2" /> Back
                </button>
                <button className="btn btn-primary btn-lg px-5 shadow-sm" onClick={nextStep} style={{ borderRadius: "10px" }}>
                  Next Step <FaArrowRight className="ms-2" />
                </button>
              </div>
            </div>
          )}

          {/* STEP 3: EMPLOYMENT DETAILS */}
          {step === 3 && (
            <div className="animate__animated animate__fadeIn">
              <h4 className="mb-4 d-flex align-items-center" style={{ color: "#0077cc" }}>
                <FaBriefcase className="me-2" /> Employment Details
              </h4>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label fw-bold">Branch *</label>
                  <input type="text" name="branch" className="form-control form-control-lg border-2" value={formData.employment.branch} onChange={handleEmploymentChange} required placeholder="Assigned branch" />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-bold">Designation *</label>
                  <input type="text" name="designation" className="form-control form-control-lg border-2" value={formData.employment.designation} onChange={handleEmploymentChange} required placeholder="Job title" />
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-bold">Employee Type</label>
                  <select name="employee_type" className="form-select form-select-lg" value={formData.employment.employee_type} onChange={handleEmploymentChange}>
                    <option value="">Select</option>
                    <option value="Full Time">Full Time</option>
                    <option value="Part Time">Part Time</option>
                    <option value="Contract">Contract</option>
                    <option value="Intern">Intern</option>
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-bold">Date of Joining</label>
                  <input type="date" name="date_of_joining" className="form-control form-control-lg" value={formData.employment.date_of_joining} onChange={handleEmploymentChange} />
                </div>
                <div className="col-md-4 d-flex align-items-end">
                  <div className="form-check form-switch mb-2 h-100 d-flex align-items-center">
                    <input className="form-check-input me-2" type="checkbox" name="is_active" checked={formData.employment.is_active} onChange={handleEmploymentChange} id="isActive" style={{ width: "50px", height: "25px" }} />
                    <label className="form-check-label fw-bold" htmlFor="isActive">Active Status</label>
                  </div>
                </div>
              </div>

              <hr className="my-5" />
              <h5 className="mb-4 d-flex align-items-center"><FaClock className="me-2" /> Working Periods</h5>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label fw-bold">Contract Start Date</label>
                  <input type="date" name="period_from" className="form-control form-control-lg" value={formData.timing.period_from} onChange={handleTimingChange} />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-bold">Contract End Date</label>
                  <input type="date" name="period_to" className="form-control form-control-lg" value={formData.timing.period_to} onChange={handleTimingChange} />
                </div>
              </div>

              <div className="d-flex justify-content-between mt-5">
                <button className="btn btn-secondary btn-lg px-4" onClick={prevStep} style={{ borderRadius: "10px" }}>
                  <FaArrowLeft className="me-2" /> Back
                </button>
                <button className="btn btn-primary btn-lg px-5 shadow-sm" onClick={nextStep} style={{ borderRadius: "10px" }}>
                  Next Step <FaArrowRight className="ms-2" />
                </button>
              </div>
            </div>
          )}

          {/* STEP 4: SHIFT DETAILS */}
          {step === 4 && (
            <div className="animate__animated animate__fadeIn">
              <h4 className="mb-4 d-flex align-items-center" style={{ color: "#0077cc" }}>
                <FaClock className="me-2" /> Shift & Finalization
              </h4>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label fw-bold">Default Shift Name</label>
                  <input type="text" name="shift_name" className="form-control form-control-lg" value={formData.shift.shift_name} onChange={handleShiftChange} placeholder="e.g. Day Shift, Morning" />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-bold">Total Daily Hours</label>
                  <input type="number" name="total_hours" className="form-control form-control-lg" value={formData.shift.total_hours} onChange={handleShiftChange} placeholder="8" />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-bold">Work Start Time</label>
                  <input type="time" name="start_time" className="form-control form-control-lg" value={formData.shift.start_time} onChange={handleShiftChange} />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-bold">Work End Time</label>
                  <input type="time" name="end_time" className="form-control form-control-lg" value={formData.shift.end_time} onChange={handleShiftChange} />
                </div>
              </div>

              <div className="mt-5 p-4 rounded bg-light border border-primary border-opacity-25 d-flex align-items-center">
                <div className="text-primary fs-3 me-3"><FaCheckCircle /></div>
                <div>
                  <h6 className="mb-1 fw-bold">Ready to submit?</h6>
                  <p className="mb-0 text-muted small">By clicking submit, you will create a full record including profile, contact, employment, and timing details.</p>
                </div>
              </div>

              <div className="d-flex justify-content-between mt-5">
                <button className="btn btn-secondary btn-lg px-4" onClick={prevStep} disabled={loading} style={{ borderRadius: "10px" }}>
                  <FaArrowLeft className="me-2" /> Back
                </button>
                <button className="btn btn-success btn-lg px-5 shadow" onClick={handleSubmit} disabled={loading} style={{ borderRadius: "10px", background: "#28a745", border: "none" }}>
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Saving Record...
                    </>
                  ) : (
                    <>Submit Full Record <FaCheckCircle className="ms-2" /></>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmployeeWizard;
